import { useCallback, useEffect, useRef, useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import { createMixedRecordingStream } from '@/entities/recording';

import { API } from '@/shared/api';
import { useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceDate } from '@/shared/i18n/formatLocale';

import { ChunkUploadQueueState, createChunkUploadQueue } from './lib/chunkUploadQueue';
import {
  RecordingCancelReason,
  RecordingEndReason,
  RecordingStartStep,
  trackCancelled,
  trackCompleted,
  trackPermission,
  trackSaved,
  trackStarted,
  trackStartFailure,
  trackUploadFailure,
} from './lib/recorderAnalytics';
import {
  createSharingEndedError,
  getRecorderErrorMessage,
  getRecordingSaveBlocker,
  RecordingSaveBlocker,
} from './lib/recorderErrors';
import {
  createEmptyRecordingMedia,
  createRecorders,
  isCapturingCurrentTab,
  pauseRecorders,
  releaseRecordingMedia,
  requestCameraAndMicrophone,
  requestScreenShare,
  resumeRecorders,
  startRecorders,
  stopRecorders,
} from './lib/recorderMedia';
import { getRecordingDurationS } from './lib/recordingFinish';
import {
  abortServerRecordingQuietly,
  createIdleSession,
  RecordingSession,
  saveRecording,
  takeAnalytics,
} from './model/recordingSession';
import { useMountedRef } from './model/useMountedRef';
import { useRecorderDevices } from './model/useRecorderDevices';
import { useRecordingClock } from './model/useRecordingClock';

export type AppScreenRecorderStatus =
  | 'completed'
  | 'discarding'
  | 'error'
  | 'finishing'
  | 'paused'
  | 'recording'
  | 'saveFailed'
  | 'setup'
  | 'starting';

/** Leaving the page would lose the recording in these statuses. */
const UNLOAD_GUARDED_STATUSES: AppScreenRecorderStatus[] = [
  'finishing',
  'paused',
  'recording',
  'saveFailed',
  'starting',
];

const SAVE_RETRY_DELAYS_MS = [2_000, 5_000] as const;

const canStart = (status: AppScreenRecorderStatus) => status === 'setup' || status === 'error';
const isCapturing = (status: AppScreenRecorderStatus) => status === 'recording' || status === 'paused';
const isStopped = (status: AppScreenRecorderStatus) =>
  status === 'discarding' || status === 'finishing' || status === 'saveFailed';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type AppScreenRecorderOptions = {
  /** Saves the recording on its own once it is this long (the free/trial limit). */
  maxDurationSeconds?: number;
  /** The space the recording is created and saved in, whatever page the user moves to meanwhile. */
  spaceId?: string;
};

/**
 * Records the screen, with an optional camera and microphone, and uploads it in chunks while recording.
 * Status flow: setup → starting → recording ⇄ paused → finishing → completed. A failed start ends in error, from
 * which start() can be retried. A failed save ends in saveFailed, which keeps the recording on the server and the
 * unsent chunks in the tab: retrySave() tries again, discard() deletes it. discard() goes back to setup from anywhere.
 * A save the server refuses for good (no free recording minutes left, no access to the space) is not retried:
 * saveBlocker tells why, and only discard() is left.
 * When the upload keeps failing while recording, the recording stops by itself and ends in saveFailed.
 */
export const useAppScreenRecorder = ({ maxDurationSeconds, spaceId = '' }: AppScreenRecorderOptions = {}) => {
  const { locale, t } = useInterfaceLanguage();
  const [status, setStatus] = useState<AppScreenRecorderStatus>('setup');
  const [error, setError] = useState<null | string>(null);
  const [completedRecordingId, setCompletedRecordingId] = useState<null | string>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [saveBlocker, setSaveBlocker] = useState<null | RecordingSaveBlocker>(null);

  const mountedRef = useMountedRef();
  const statusRef = useRef<AppScreenRecorderStatus>('setup');
  // One mutable object for the whole hook lifetime: reset in place, never replaced.
  const sessionRef = useRef<RecordingSession>(createIdleSession());
  // Discard and unmount bump it so a start() still waiting for a permission or a request gives up.
  const startAttemptRef = useRef(0);
  // The screen-share 'ended' listener and the limit timer outlive renders, so they call the latest finish().
  const finishRef = useRef<(endReason: RecordingEndReason) => Promise<void>>(async () => undefined);
  // For the unmount cleanup, whose closure is from the first render.
  const spaceIdRef = useRef(spaceId);
  spaceIdRef.current = spaceId;

  const { clearPreview, showPreview, ...devices } = useRecorderDevices(status === 'setup');
  const { cameraEnabled, microphoneEnabled, selectedCameraId, selectedMicrophoneId } = devices;
  const { clock, durationSeconds } = useRecordingClock({
    isTicking: status === 'recording',
    maxDurationSeconds,
    onLimitReached: () => {
      // A recording being discarded keeps ticking until the discard settles; it must not be saved instead.
      if (!sessionRef.current.upload?.isAborted()) void finishRef.current('limitMinutesReached');
    },
  });

  const updateStatus = useCallback(
    (nextStatus: AppScreenRecorderStatus) => {
      statusRef.current = nextStatus;
      if (mountedRef.current) setStatus(nextStatus);
    },
    [mountedRef],
  );

  const trackCancel = useCallback(
    (cancelReason: RecordingCancelReason) => {
      const session = sessionRef.current;
      if (takeAnalytics(session)) trackCancelled(cancelReason, clock.getElapsedMs(), session.recordingId);
    },
    [clock],
  );

  const releaseMedia = useCallback(() => {
    clearPreview();
    releaseRecordingMedia(sessionRef.current.media);
    sessionRef.current.media = createEmptyRecordingMedia();
  }, [clearPreview]);

  const abortServerRecording = useCallback(async () => {
    const { recordingId } = sessionRef.current;
    sessionRef.current.recordingId = null;
    if (recordingId) await abortServerRecordingQuietly(recordingId, spaceId);
  }, [spaceId]);

  const resetSession = useCallback(() => {
    Object.assign(sessionRef.current, createIdleSession());
    clock.reset();
    if (mountedRef.current) setIsReconnecting(false);
  }, [clock, mountedRef]);

  /** Throws the current recording away: drops pending uploads, releases the media, aborts it on the server. */
  const abandonSession = useCallback(async () => {
    sessionRef.current.upload?.abort();
    releaseMedia();
    await abortServerRecording();
    resetSession();
  }, [abortServerRecording, releaseMedia, resetSession]);

  const start = useCallback(async () => {
    if (!canStart(statusRef.current)) return;
    const attempt = ++startAttemptRef.current;
    const isCancelled = () => attempt !== startAttemptRef.current;
    const session = sessionRef.current;
    setError(null);
    setSaveBlocker(null);
    setCompletedRecordingId(null);
    updateStatus('starting');
    clearPreview();
    let step: RecordingStartStep = 'screenShare';

    // Every await below may outlive a discard or an unmount. A cancelled start releases only what it has just
    // acquired: whatever it stored in the session before was already released by the discard or unmount.
    try {
      const display = await requestScreenShare();
      if (isCancelled()) {
        releaseRecordingMedia({ display });
        return;
      }
      session.media.display = display;
      trackPermission('screenShare', true);
      step = 'media';

      const [camera, microphone] = await requestCameraAndMicrophone({
        cameraEnabled,
        microphoneEnabled,
        selectedCameraId,
        selectedMicrophoneId,
      });
      if (isCancelled()) {
        releaseRecordingMedia({ camera, microphone });
        return;
      }
      if (cameraEnabled) trackPermission('camera', Boolean(camera));
      if (microphoneEnabled) trackPermission('microphone', Boolean(microphone));
      Object.assign(session.media, { camera, microphone });
      showPreview(camera);
      if (cameraEnabled && !camera) {
        enqueueSnackbar(t('recordingModals.recorder.cameraUnavailable'), { variant: 'info' });
      }
      if (microphoneEnabled && !microphone) {
        enqueueSnackbar(t('recordingModals.recorder.microphoneUnavailable'), { variant: 'info' });
      }

      const mixed = await createMixedRecordingStream(display, microphone);
      if (isCancelled()) {
        releaseRecordingMedia({ audioContext: mixed.audioContext, mixed: mixed.stream });
        return;
      }
      Object.assign(session.media, { audioContext: mixed.audioContext, mixed: mixed.stream });

      step = 'upload';
      const title = t('recordingModals.recorder.defaultTitle', {
        date: formatInterfaceDate(locale, new Date(), { dateStyle: 'medium', timeStyle: 'short' }),
      });
      const { id: recordingId } = await API.createRecording({ ...(spaceId ? { space_id: spaceId } : {}), title });
      if (isCancelled()) {
        await abortServerRecordingQuietly(recordingId, spaceId);
        return;
      }
      session.recordingId = recordingId;
      const recordedCamera = isCapturingCurrentTab(display) ? null : camera;
      await API.initRecordingUpload(recordingId, Boolean(recordedCamera), spaceId);
      if (isCancelled()) return;
      step = 'media';

      const handleUploadStateChange = (uploadState: ChunkUploadQueueState) => {
        if (mountedRef.current) setIsReconnecting(uploadState === 'retrying');
        if (uploadState === 'failed' && isCapturing(statusRef.current)) {
          void Promise.resolve().then(() => finishRef.current('uploadFailed'));
        }
      };
      session.upload = createChunkUploadQueue(recordingId, API.uploadRecordingChunk, {
        onStateChange: handleUploadStateChange,
      });
      if (display.getVideoTracks()[0]?.readyState === 'ended') {
        throw createSharingEndedError(t('recordingModals.recorder.error.sharingEnded'));
      }
      session.recorders = createRecorders({ camera: recordedCamera, mixed: mixed.stream }, (track, blob) =>
        session.upload?.enqueue(track, blob),
      );
      // The browser's own "Stop sharing" button saves the recording like the Stop button does.
      display
        .getVideoTracks()[0]
        ?.addEventListener('ended', () => window.setTimeout(() => void finishRef.current('userStopped'), 0), {
          once: true,
        });

      startRecorders(session.recorders);
      clock.start();
      updateStatus('recording');
      session.analytics = { hasCamera: Boolean(camera), hasMicrophone: Boolean(microphone) };
      trackStarted(session.analytics, recordingId);
    } catch (startError) {
      if (isCancelled()) return;
      // Before the session is abandoned: a failure after the server recording was created carries its id.
      trackStartFailure(step, startError, session.recordingId);
      await abandonSession();
      if (mountedRef.current) setError(getRecorderErrorMessage(startError, t));
      updateStatus('error');
    }
  }, [
    abandonSession,
    cameraEnabled,
    clearPreview,
    clock,
    locale,
    microphoneEnabled,
    mountedRef,
    selectedCameraId,
    selectedMicrophoneId,
    showPreview,
    spaceId,
    t,
    updateStatus,
  ]);

  const pause = useCallback(() => {
    if (statusRef.current !== 'recording') return;
    pauseRecorders(sessionRef.current.recorders);
    clock.pause();
    updateStatus('paused');
  }, [clock, updateStatus]);

  const resume = useCallback(() => {
    if (statusRef.current !== 'paused') return;
    resumeRecorders(sessionRef.current.recorders);
    clock.resume();
    updateStatus('recording');
  }, [clock, updateStatus]);

  const save = useCallback(async () => {
    const session = sessionRef.current;
    const { recordingId, stopped, upload } = session;
    const isDiscarded = () => session.stopped !== stopped || Boolean(upload?.isAborted());

    try {
      await upload?.waitForUploads();
      if (isDiscarded()) return;
    } catch (uploadError) {
      if (isDiscarded()) return;
      trackUploadFailure(uploadError, recordingId);
      updateStatus('saveFailed');
      return;
    }

    for (let attempt = 0; ; attempt += 1) {
      try {
        if (!recordingId || !stopped) throw new Error('There is no stopped recording to save.');
        // eslint-disable-next-line no-await-in-loop -- each attempt waits for the previous one to fail
        await saveRecording({ recordingId, spaceId, stopped });
        break;
      } catch (saveError) {
        if (isDiscarded()) return;
        const blocker = getRecordingSaveBlocker(saveError);
        if (blocker || attempt >= SAVE_RETRY_DELAYS_MS.length) {
          trackUploadFailure(saveError, recordingId);
          if (blocker && mountedRef.current) {
            setSaveBlocker(blocker);
            if (blocker === 'accessDenied') setError(t('recordingModals.recorder.saveFailed.accessDenied'));
          }
          updateStatus('saveFailed');
          return;
        }
        // eslint-disable-next-line no-await-in-loop -- each attempt waits for the previous one to fail
        await wait(SAVE_RETRY_DELAYS_MS[attempt]);
        if (isDiscarded()) return;
      }
    }
    // A delete confirmed while the last request was on its way wins: the discard resets the session and the status.
    if (isDiscarded() || !recordingId) return;

    trackSaved(recordingId);
    session.recordingId = null;
    resetSession();
    if (mountedRef.current) {
      setError(null);
      setCompletedRecordingId(recordingId);
    }
    updateStatus('completed');
  }, [mountedRef, resetSession, spaceId, t, updateStatus]);

  const finish = useCallback(
    async (endReason: RecordingEndReason) => {
      if (!isCapturing(statusRef.current)) return;
      updateStatus('finishing');
      const session = sessionRef.current;
      const recordingDurationS = getRecordingDurationS(clock.stop());
      const analytics = takeAnalytics(session);
      if (analytics) trackCompleted(analytics, endReason, recordingDurationS, session.recordingId);

      const { upload } = session;
      await stopRecorders(session.recorders);
      if (session.upload !== upload || upload?.isAborted()) return;
      session.stopped = { hasCamera: Boolean(session.recorders.camera), isUploadCompleted: false, recordingDurationS };
      releaseMedia();
      if (endReason === 'uploadFailed' && mountedRef.current) {
        setError(t('recordingModals.recorder.saveFailed.autoStopped'));
      }
      await save();
    },
    [clock, mountedRef, releaseMedia, save, t, updateStatus],
  );

  const retrySave = useCallback(async () => {
    if (statusRef.current !== 'saveFailed' || saveBlocker) return;
    updateStatus('finishing');
    sessionRef.current.upload?.retry();
    await save();
  }, [save, saveBlocker, updateStatus]);

  finishRef.current = finish;

  const stop = useCallback(() => finish('userStopped'), [finish]);

  const discard = useCallback(async () => {
    if (statusRef.current === 'discarding') return;
    updateStatus('discarding');
    trackCancel('deleteClicked');
    startAttemptRef.current += 1;
    // Aborting first drops the last chunks the recorders hand over while stopping.
    sessionRef.current.upload?.abort();
    await stopRecorders(sessionRef.current.recorders);
    await abandonSession();
    clock.clearDisplayedDuration();
    if (mountedRef.current) {
      setError(null);
      setSaveBlocker(null);
    }
    updateStatus('setup');
  }, [abandonSession, clock, mountedRef, trackCancel, updateStatus]);

  const isUnloadGuarded = UNLOAD_GUARDED_STATUSES.includes(status);
  useEffect(() => {
    if (!isUnloadGuarded) return;
    const preventClose = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', preventClose);
    return () => window.removeEventListener('beforeunload', preventClose);
  }, [isUnloadGuarded]);

  useEffect(
    () => () => {
      startAttemptRef.current += 1;
      const session = sessionRef.current;
      if (isStopped(statusRef.current)) {
        releaseMedia();
        return;
      }
      trackCancel('tabClosed');
      session.upload?.abort();
      void stopRecorders(session.recorders);
      releaseMedia();
      if (session.recordingId) void API.abortRecordingUpload(session.recordingId, spaceIdRef.current);
    },
    [releaseMedia, trackCancel],
  );

  return {
    ...devices,
    completedRecordingId,
    discard,
    durationSeconds,
    error,
    isReconnecting,
    pause,
    resume,
    retrySave,
    start,
    status,
    stop,
    saveBlocker,
  };
};
